import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import type { ApiEnvelope } from "@/lib/types";
import type { FrontierHome, GateReconcilePlan } from "./types";

export const FRONTIER_HOME_KEY = "ops-frontier-home";
export const GATE_RECONCILE_KEY = "ops-gates-reconcile";

const GATE_RECONCILE_URL = "/ops/gates/reconcile/";

/**
 * The Frontier home. The tenant headers decide the scope; `customerId` only
 * narrows it (the backend never widens a scope from a query parameter).
 */
export function useFrontierHome(customerId: string | null) {
  return useQuery({
    queryKey: [FRONTIER_HOME_KEY, customerId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<FrontierHome>>("/ops/home/", {
        params: customerId ? { customer: customerId } : {},
      });
      return data.data;
    },
  });
}

/**
 * The dry run of bringing the steps up to date, over the session's reach. It
 * checks every project's criteria, so it is read only while `enabled` (the
 * dialog is open), never on every load of the home; only the platform owner
 * may ask for it (the backend refuses, and records, anyone else).
 */
export function useGateReconcilePlan(enabled: boolean) {
  return useQuery({
    queryKey: [GATE_RECONCILE_KEY],
    queryFn: async () => {
      const { data } =
        await api.get<ApiEnvelope<GateReconcilePlan>>(GATE_RECONCILE_URL);
      return data.data;
    },
    enabled,
  });
}

export interface GateReconcileConfirmation {
  /** The projects the dialog showed as changing. */
  projectIds: string[];
  /** And how many they were. */
  expectedCount: number;
}

/**
 * Apply exactly the list the dialog shows: if the projects that would change
 * are no longer exactly those, the answer is 409 `plan_changed` and nothing
 * moves. Either way the dry run is read again; once applied, the home is too.
 */
export function useApplyGateReconcile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectIds,
      expectedCount,
    }: GateReconcileConfirmation) => {
      const { data } = await api.post<ApiEnvelope<GateReconcilePlan>>(
        GATE_RECONCILE_URL,
        { project_ids: projectIds, expected_count: expectedCount },
      );
      return data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [FRONTIER_HOME_KEY] });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [GATE_RECONCILE_KEY] });
    },
  });
}
